import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "modalQuery",
    "results",
    "emptyState",
    "addedWrapper",
    "addedList"
  ]

  static values = {
    url: String
  }

  connect() {
    this._debounceTimer = null
    this._addedIds = new Set()
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    this.clearResults()
    this.emptyStateTarget.classList.add("hidden")
    this.modalQueryTarget.value = ""
    setTimeout(() => this.modalQueryTarget.focus(), 50)
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  clearModalQuery() {
    this.modalQueryTarget.value = ""
    this.clearResults()
    this.emptyStateTarget.classList.add("hidden")
    this.modalQueryTarget.focus()
  }

  onKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation?.()
      return
    }
    if (e.key === "Escape") this.close()
  }

  search() {
    const q = this.modalQueryTarget.value.trim()
    clearTimeout(this._debounceTimer)

    if (q.length < 2) {
      this.clearResults()
      this.emptyStateTarget.classList.add("hidden")
      return
    }

    this._debounceTimer = setTimeout(() => this.fetchResults(q), 250)
  }

  async fetchResults(q) {
    if (!this.urlValue) {
      console.error(
        "[funds-advanced-search] urlValue is missing. Check data-funds-advanced-search-url-value in the DOM."
      )
      return
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)
    url.searchParams.set("subscription_form_search", "1")

    const res = await fetch(url.toString(), {
      headers: { Accept: "application/json" }
    })

    if (!res.ok) return

    const data = await res.json()
    const funds = data?.funds || []

    this.renderResults(funds)
  }

  renderResults(funds) {
    this.resultsTarget.innerHTML = ""

    if (funds.length === 0) {
      this.emptyStateTarget.classList.remove("hidden")
      return
    }

    this.emptyStateTarget.classList.add("hidden")

    funds.forEach((f) => {
      const isInEOS = !!f.in_eos_category
      const alreadyAdded = this._addedIds.has(String(f.id))

      const el = document.createElement("div")

      // Carte "verte" si déjà dans EOS, sinon carte standard
      el.className = isInEOS
        ? "rounded-sm border border-green-200 bg-green-50 px-4 py-3 flex items-start justify-between gap-4"
        : "rounded-sm border border-gray-200 bg-white px-4 py-3 flex items-start justify-between gap-4"

      // Colonne gauche (infos)
      const left = `
        <div class="min-w-0">
          <div class="text-sm font-semibold text-oxford-900 truncate">
            ${this.escapeHtml(f.name || "")}
          </div>

          <div class="text-xs text-gray-600 mt-1">
            ${this.escapeHtml(f.company_name || "Société inconnue")}
            ${Array.isArray(f.isins) && f.isins.length
          ? ` • <span class="text-gray-500">${this.escapeHtml(f.isins.join(", "))}</span>`
          : ""
        }
          </div>
        </div>
      `

      // Colonne droite (bouton / état)
      let right = ""

      if (isInEOS) {
        right = `
          <div class="shrink-0 text-right">
            <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-green-800 bg-green-100 border border-green-200 px-2 py-0.5 rounded-full whitespace-nowrap">
              Disponible dans la catégorie "${this.escapeHtml(f.eos_category_name || "—")}"
            </span>
          </div>
        `
      } else {
        const disabled = alreadyAdded
        right = `
          <button type="button"
                  class="shrink-0 px-3 py-2 text-sm rounded-sm border ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
          }"
                  data-action="click->funds-advanced-search#add"
                  data-fund-id="${this.escapeHtml(String(f.id))}"
                  data-fund-name="${this.escapeHtml(f.name || "")}"
                  data-fund-company="${this.escapeHtml(f.company_name || "")}">
            ${disabled ? "Ajouté" : "Ajouter"}
          </button>
        `
      }

      el.innerHTML = `${left}${right}`
      this.resultsTarget.appendChild(el)
    })
  }

  add(event) {
    const btn = event.currentTarget
    const fundId = btn.dataset.fundId
    const name = btn.dataset.fundName
    const company = btn.dataset.fundCompany

    if (!fundId || this._addedIds.has(String(fundId))) return

    this._addedIds.add(String(fundId))
    btn.textContent = "Ajouté"
    btn.classList.add("opacity-50", "cursor-not-allowed")

    this.addedWrapperTarget.classList.remove("hidden")

    const li = document.createElement("li")
    li.className =
      "group/fund relative rounded-md border border-gray-200 bg-cream hover:bg-pine-50/50 transition-colors"
    li.dataset.fundId = fundId

    li.innerHTML = `
      <div class="absolute left-0 top-0 h-full w-1 rounded-l-md bg-orange-500"></div>

      <div class="grid grid-cols-12 gap-4 px-4 py-3 items-center">
        <div class="col-span-12 md:col-span-7 flex items-center min-w-0">
          <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-800 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
            Soumis à examen
          </span>

          <div class="ml-3 min-w-0">
            <div class="text-sm font-semibold text-oxford-900 truncate">
              ${this.escapeHtml(name)}
            </div>
            <div class="text-xs text-gray-600 truncate">
              ${this.escapeHtml(company)}
            </div>
          </div>
        </div>

        <div class="col-span-12 md:col-span-5 flex md:justify-end">
          <button type="button"
                  class="px-2 py-1 text-xs rounded-sm border border-gray-300 hover:bg-gray-50"
                  data-action="click->funds-advanced-search#remove"
                  data-fund-id="${this.escapeHtml(String(fundId))}">
            Retirer
          </button>
        </div>
      </div>
    `

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "subscription[extra_fund_ids][]"
    input.value = fundId
    input.dataset.fundId = fundId
    input.dataset.fundName = name || ""
    input.dataset.fundCompany = company || ""

    this.element.appendChild(input)
    this.addedListTarget.appendChild(li)
    this.dispatchSelectionChanged()
  }

  remove(event) {
    const fundId = event.currentTarget.dataset.fundId
    if (!fundId) return

    this._addedIds.delete(String(fundId))

    const li = this.addedListTarget.querySelector(
      `li[data-fund-id="${CSS.escape(String(fundId))}"]`
    )
    if (li) li.remove()

    const input = this.element.querySelector(
      `input[type="hidden"][name="subscription[extra_fund_ids][]"][data-fund-id="${CSS.escape(
        String(fundId)
      )}"]`
    )
    if (input) input.remove()

    if (this.addedListTarget.children.length === 0) {
      this.addedWrapperTarget.classList.add("hidden")
    }

    this.dispatchSelectionChanged()
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
  }

  escapeHtml(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }

  dispatchSelectionChanged() {
    this.dispatch("selection-changed")
  }
}
